import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { debounce, map } from 'lodash'
import {
	Button, Empty, Form, Input, InputNumber, Popconfirm, Select, Table, Tag
} from 'antd'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import {
	CalendarOutlined, CaretDownOutlined, CloseCircleOutlined, DeleteOutlined, QuestionCircleOutlined
} from '@ant-design/icons'
import moment from 'moment'

import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import { FilterValue, SorterResult } from 'antd/lib/table/interface'
import {
	NumberParam, StringParam, useQueryParams, withDefault
} from 'use-query-params'
import { RootState } from '../../reducers'
import { STORE_ITEM_TRANSACTION_TYPE, translateStoreItemTransactionTypeToSk } from '../../utils/enums'
import queryKeys from '../../queries/queryKeys'
import fetchStoreItemTransactionsList from '../../queries/storeItemTransactions/fetchStoreItemTransactions'
import deleteStoreItemTransaction from '../../queries/storeItemTransactions/deleteStoreItemTransaction'
import useInvalidateMutation from '../../queries/useInvalidateMutation'
import {
	fetchJobOptions, fetchStoreItemOptions, fetchUserOptions
} from '../../queries/storeItemTransactions/fetchTransactionFilterOptions'
import RemoteSelectFilter from './components/RemoteSelectFilter'
import DateRangePresetPicker, { DateRangeValue } from '../managerTasks/DateRangePresetPicker'
import { StoreItemTransactionTableItem } from './types'

const PAGE_SIZE = 200

const TRANSACTION_TYPES = [
	STORE_ITEM_TRANSACTION_TYPE.REGULAR,
	STORE_ITEM_TRANSACTION_TYPE.IN,
	STORE_ITEM_TRANSACTION_TYPE.CONSUME
]

const TYPE_COLORS: Record<string, string> = {
	[STORE_ITEM_TRANSACTION_TYPE.REGULAR]: 'blue',
	[STORE_ITEM_TRANSACTION_TYPE.IN]: 'green',
	[STORE_ITEM_TRANSACTION_TYPE.CONSUME]: 'red'
}

type Columns = ColumnsType<StoreItemTransactionTableItem>

const StoreItemTransactions = () => {
	const { t } = useTranslation('pages')

	const profile = useSelector((state: RootState) => state.profile.profile.data)

	const isSuperAdmin = String(profile?.role) === 'SUPERADMIN'
	const canDelete = isSuperAdmin || !!profile?.hasPermissionToRemoveStoreItemTransaction

	const [query, setQuery] = useQueryParams({
		search: StringParam,
		limit: withDefault(NumberParam, PAGE_SIZE),
		page: withDefault(NumberParam, 1),
		orderBy: withDefault(StringParam, 'createdAt'),
		orderDirection: withDefault(StringParam, 'desc'),
		amount: NumberParam,
		type: StringParam,
		jobFromID: NumberParam,
		jobToID: NumberParam,
		userExecutorID: NumberParam,
		storeItemID: NumberParam,
		startDate: StringParam,
		endDate: StringParam
	})

	const { data: transactionsData, isLoading: isLoadingList } = useQuery({
		queryKey: queryKeys.storeItemTransactions.list({
			limit: PAGE_SIZE,
			page: query.page,
			search: query.search,
			orderBy: query.orderBy,
			orderDirection: query.orderDirection,
			amount: query.amount,
			type: query.type,
			jobFromID: query.jobFromID,
			jobToID: query.jobToID,
			userExecutorID: query.userExecutorID,
			storeItemID: query.storeItemID,
			startDate: query.startDate,
			endDate: query.endDate
		}),
		queryFn: () => fetchStoreItemTransactionsList({
			...query,
			limit: PAGE_SIZE
		}),
		keepPreviousData: true
	})

	const dataSource = transactionsData || { tableData: [], pagination: null }

	const { mutate: removeTransaction, isLoading: isRemoving } = useInvalidateMutation({
		mutationFn: (id: number) => deleteStoreItemTransaction(id),
		invalidateKeys: [['storeItemTransactions', 'list']]
	})

	const resetFilter = () => {
		setQuery({
			search: null,
			limit: PAGE_SIZE,
			page: 1,
			orderBy: 'createdAt',
			orderDirection: 'desc',
			amount: null,
			type: null,
			jobFromID: null,
			jobToID: null,
			userExecutorID: null,
			storeItemID: null,
			startDate: null,
			endDate: null
		})
	}

	const isFilterActive = !!query.search
		|| (query.amount !== null && query.amount !== undefined)
		|| !!query.type
		|| !!query.jobFromID
		|| !!query.jobToID
		|| !!query.userExecutorID
		|| !!query.storeItemID
		|| !!query.startDate
		|| !!query.endDate

	// zoznamy vo filtroch sa nacitavaju po strankach - nazov zvolenej hodnoty (napr. po obnoveni
	// stranky z URL) preto berieme z uz nacitaneho riadku tabulky
	const firstRow = dataSource.tableData[0]

	const dateRange: DateRangeValue | null = query.startDate && query.endDate
		? [moment(query.startDate), moment(query.endDate)]
		: null

	const columns: Columns = [
		{
			title: i18next.t('pages:id'),
			dataIndex: 'id',
			key: 'id',
			ellipsis: true,
			width: 70,
			sorter: true,
			render: (value) => value || '-'
		},
		{
			title: i18next.t('pages:Dátum'),
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 160,
			sorter: true,
			defaultSortOrder: 'descend',
			render: (value) => value || '-'
		},
		{
			title: i18next.t('pages:Typ'),
			dataIndex: 'type',
			key: 'type',
			width: 120,
			sorter: true,
			render(value: STORE_ITEM_TRANSACTION_TYPE) {
				return value
					? <Tag color={TYPE_COLORS[value]}>{translateStoreItemTransactionTypeToSk(value)}</Tag>
					: '-'
			}
		},
		{
			title: i18next.t('pages:Položka'),
			dataIndex: 'storeItemName',
			key: 'storeItemName',
			ellipsis: true,
			width: 200,
			render: (value) => value || '-'
		},
		{
			title: i18next.t('pages:Množstvo'),
			dataIndex: 'amount',
			key: 'amount',
			width: 110,
			sorter: true,
			render: (value) => (value ?? '-')
		},
		{
			title: i18next.t('pages:Zákazka z'),
			dataIndex: 'jobFromName',
			key: 'jobFromName',
			ellipsis: true,
			width: 210,
			render: (value) => value || '-'
		},
		{
			title: i18next.t('pages:Zákazka do'),
			dataIndex: 'jobToName',
			key: 'jobToName',
			ellipsis: true,
			width: 210,
			render: (value) => value || '-'
		},
		{
			title: i18next.t('pages:Vykonal'),
			dataIndex: 'userExecutorName',
			key: 'userExecutorName',
			ellipsis: true,
			width: 200,
			render: (value) => value || '-'
		},
		...(canDelete ? [{
			title: '',
			key: 'operation',
			fixed: 'right' as const,
			width: 50,
			render(text: any, record: StoreItemTransactionTableItem) {
				return (
					<Popconfirm
						title={i18next.t('pages:Skutočne chcete trvale vymazať tranzakciu? Stav skladu sa prepočíta.')}
						icon={<QuestionCircleOutlined style={{ color: 'red' }}/>}
						cancelText={i18next.t('pages:Zrušiť')}
						okText={i18next.t('pages:Vymazať')}
						onConfirm={(e) => {
							e?.stopPropagation()
							removeTransaction(record.id)
						}}
						onCancel={(e) => e?.stopPropagation()}
						okButtonProps={{
							size: 'small',
							type: 'primary'
						}}
						cancelButtonProps={{
							size: 'small',
							type: 'ghost'
						}}
					>
						<Button
							icon={<DeleteOutlined/>}
							type={'primary'}
							loading={isRemoving}
							onClick={(e) => e.stopPropagation()}
						/>
					</Popconfirm>
				)
			}
		}] : [])
	]

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue | null>,
		sorter: SorterResult<StoreItemTransactionTableItem> | SorterResult<StoreItemTransactionTableItem>[]
	) => {
		let order = {}
		if ((sorter as SorterResult<any>)?.order) {
			const sorterLocal = (sorter as SorterResult<any>)
			order = {
				orderBy: sorterLocal.field,
				orderDirection: sorterLocal.order === 'ascend' ? 'asc' : 'desc'
			}
		}

		setQuery({
			...query,
			limit: PAGE_SIZE,
			page: pagination.current,
			...order
		})
	}

	const debounced = useCallback(debounce((searchTerm: string) => setQuery({
		...query,
		search: searchTerm,
		page: 1
	}), 300), [query])

	const handleOnChange = ((e: React.ChangeEvent<HTMLInputElement>) => {
		debounced(e.target.value)
	})

	return (
		<div className={'page-wrapper'}>
			<div className={'flex justify-between'}>
				<Form.Item>
					<Input.Search
						onChange={handleOnChange}
						defaultValue={query.search || ''}
						placeholder={t('pages:Položka, adresa, zamestnanec')}
						style={{ width: 300 }}
						allowClear
					/>
				</Form.Item>
			</div>
			<div className={'flex'} style={{ flexWrap: 'wrap', gap: 8 }}>
				<Form.Item>
					<RemoteSelectFilter
						value={query.storeItemID ?? null}
						onChange={(value) => setQuery({ ...query, storeItemID: value, page: 1 })}
						placeholder={t('pages:Položka')}
						queryKey={queryKeys.storeItemTransactions.storeItemOptions}
						fetcher={fetchStoreItemOptions}
						fallbackLabel={firstRow?.storeItemName}
					/>
				</Form.Item>

				<Form.Item>
					<RemoteSelectFilter
						value={query.jobFromID ?? null}
						onChange={(value) => setQuery({ ...query, jobFromID: value, page: 1 })}
						placeholder={t('pages:Zákazka z')}
						queryKey={queryKeys.storeItemTransactions.jobOptions}
						fetcher={fetchJobOptions}
						fallbackLabel={firstRow?.jobFromName}
					/>
				</Form.Item>

				<Form.Item>
					<RemoteSelectFilter
						value={query.jobToID ?? null}
						onChange={(value) => setQuery({ ...query, jobToID: value, page: 1 })}
						placeholder={t('pages:Zákazka do')}
						queryKey={queryKeys.storeItemTransactions.jobOptions}
						fetcher={fetchJobOptions}
						fallbackLabel={firstRow?.jobToName}
					/>
				</Form.Item>

				<Form.Item>
					<RemoteSelectFilter
						value={query.userExecutorID ?? null}
						onChange={(value) => setQuery({ ...query, userExecutorID: value, page: 1 })}
						placeholder={t('pages:Kto vykonal')}
						queryKey={queryKeys.storeItemTransactions.userOptions}
						fetcher={fetchUserOptions}
						fallbackLabel={firstRow?.userExecutorName}
					/>
				</Form.Item>

				<Form.Item>
					<Select
						className={'dropDown'}
						size={'middle'}
						style={{ minWidth: 160 }}
						placeholder={t('pages:Typ')}
						value={(query.type as STORE_ITEM_TRANSACTION_TYPE) ?? undefined}
						onChange={(value) => setQuery({ ...query, type: value ?? null, page: 1 })}
						allowClear
					>
						{map(TRANSACTION_TYPES, (type) => (
							<Select.Option key={type} value={type}>
								{translateStoreItemTransactionTypeToSk(type)}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item>
					<InputNumber
						placeholder={t('pages:Množstvo')}
						style={{ width: 120 }}
						value={query.amount ?? undefined}
						min={0}
						onChange={(value) => setQuery({
							...query,
							amount: (value === null || value === undefined || value === '') ? null : Number(value),
							page: 1
						})}
					/>
				</Form.Item>

				<Form.Item>
					{dateRange === null ? (
						<button
							type="button"
							onClick={() => {
								const def: DateRangeValue = [moment().subtract(27, 'days').startOf('day'), moment().endOf('day')]
								setQuery({
									...query, startDate: def[0].toISOString(), endDate: def[1].toISOString(), page: 1
								})
							}}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 6,
								padding: '0 12px',
								height: 32,
								borderRadius: 6,
								border: '1px solid #d9d9d9',
								background: '#fff',
								fontSize: 13,
								cursor: 'pointer',
								color: '#374151'
							}}
						>
							<CalendarOutlined style={{ fontSize: 13, color: '#9ca3af' }}/>
							<span>{t('pages:Dátum')}</span>
							<CaretDownOutlined style={{ fontSize: 10, color: '#9ca3af' }}/>
						</button>
					) : (
						<DateRangePresetPicker
							value={dateRange}
							onChange={(range) => setQuery({
								...query, startDate: range[0].toISOString(), endDate: range[1].toISOString(), page: 1
							})}
							onClear={() => setQuery({
								...query, startDate: null, endDate: null, page: 1
							})}
						/>
					)}
				</Form.Item>

				{isFilterActive && (
					<Form.Item>
						<Button
							icon={<CloseCircleOutlined/>}
							onClick={resetFilter}
						>
							{t('pages:Zrušiť filter')}
						</Button>
					</Form.Item>
				)}
			</div>
			<Table
				className={'general-table'}
				columns={columns}
				dataSource={dataSource.tableData}
				onChange={handleTableChange}
				style={{ marginTop: 0 }}
				showSorterTooltip={false}
				pagination={{
					pageSize: PAGE_SIZE,
					total: dataSource.pagination?.totalCount,
					current: dataSource.pagination?.page,
					showSizeChanger: false
				}}
				loading={isLoadingList}
				locale={{
					emptyText: (
						<Empty description={t('pages:Žiadne tranzakcie')}/>
					)
				}}
				size={'small'}
			/>
		</div>
	)
}

export default StoreItemTransactions
